import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Plus, X, ChevronUp, ChevronDown, GripVertical, Lightbulb, BookOpen, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import {
  customObjectsService,
  CustomFieldDef,
  CustomFieldType,
  CustomObjectType,
} from '@/services/custom-objects.service';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Select } from '@/components/Select';

const FIELD_TYPES: Array<{ value: CustomFieldType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'money', label: 'Money' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date + time' },
  { value: 'boolean', label: 'Yes / no' },
  { value: 'select', label: 'Select (one)' },
  { value: 'multiselect', label: 'Select (multiple)' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'media', label: 'Photos & videos (max 10)' },
];

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 41);
}

export default function CustomObjectTypeEditor() {
  const { key: routeKey } = useParams<{ key?: string }>();
  const isNew = !routeKey || routeKey === 'new';
  const nav = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [pluralLabel, setPluralLabel] = useState('');
  const [icon, setIcon] = useState('Database');
  const [description, setDescription] = useState('');
  const [linkToContact, setLinkToContact] = useState(true);
  const [linkToLead, setLinkToLead] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState<CustomFieldDef[]>([]);

  // AI generate panel state — only relevant on the new-type flow.
  const [aiBrief, setAiBrief] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const generateViaAi = async () => {
    const brief = aiBrief.trim();
    if (!brief) {
      toast.error('Describe what you want to track first.');
      return;
    }
    setAiBusy(true);
    try {
      const draft = await customObjectsService.generateTypeViaAi({ description: brief });
      // Populate the form. Any existing input is overwritten — the AI panel
      // is positioned at the top of the new-type flow and is intended as the
      // primary starting point, not a partial-merge helper.
      setLabel(draft.label);
      setPluralLabel(draft.pluralLabel ?? '');
      setKey(draft.key);
      setIcon(draft.icon || 'Database');
      setDescription(draft.description ?? '');
      setLinkToContact(draft.linkToContact);
      setLinkToLead(draft.linkToLead);
      setFields(draft.fields ?? []);
      toast.success(`Generated ${draft.fields?.length ?? 0} fields — edit anything below, then Save.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Generation failed. Set a default AI model in /settings/ai.');
    } finally {
      setAiBusy(false);
    }
  };

  useEffect(() => {
    if (isNew) return;
    customObjectsService
      .getType(routeKey!)
      .then((t: CustomObjectType) => {
        setKey(t.key);
        setLabel(t.label);
        setPluralLabel(t.pluralLabel ?? '');
        setIcon(t.icon ?? 'Database');
        setDescription(t.description ?? '');
        setLinkToContact(t.linkToContact);
        setLinkToLead(t.linkToLead);
        setIsActive(t.isActive);
        setFields(t.fields ?? []);
      })
      .catch(() => toast.error('Failed to load type'))
      .finally(() => setLoading(false));
  }, [routeKey, isNew]);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { key: `field_${Date.now()}`, label: '', type: 'text', required: false, order: prev.length },
    ]);
  };

  const updateField = (idx: number, patch: Partial<CustomFieldDef>) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  const removeField = (idx: number) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveField = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= fields.length) return;
    setFields((prev) => {
      const out = [...prev];
      [out[idx], out[next]] = [out[next], out[idx]];
      return out;
    });
  };

  const onSave = async () => {
    if (!label.trim()) {
      toast.error('Label is required');
      return;
    }
    const finalKey = isNew ? slugify(key || label) : key;
    if (!finalKey) {
      toast.error('Key is required');
      return;
    }
    const finalFields = fields.map((f, i) => ({
      ...f,
      key: f.key || `field_${i}`,
      label: f.label || f.key || `Field ${i + 1}`,
      order: i,
    }));

    setSaving(true);
    try {
      if (isNew) {
        await customObjectsService.createType({
          key: finalKey,
          label,
          pluralLabel: pluralLabel || undefined,
          icon,
          description: description || undefined,
          linkToContact,
          linkToLead,
          fields: finalFields,
          isActive,
        });
        toast.success('Type created');
        nav(`/custom-objects/types/${finalKey}`);
      } else {
        await customObjectsService.updateType(finalKey, {
          label,
          pluralLabel: pluralLabel || undefined,
          icon,
          description: description || undefined,
          linkToContact,
          linkToLead,
          fields: finalFields,
          isActive,
        });
        toast.success('Type saved');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}</div>;
  }

  return (
    <>
      <Topbar
        title={isNew ? 'New custom object type' : `Edit · ${label || routeKey}`}
        breadcrumb="Data"
      />

      <div className="px-7 py-7 w-full space-y-6">
        {/* Sticky save bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface/90 px-5 py-3 shadow-soft backdrop-blur-sm">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => nav('/custom-objects')}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:border-primary/50 hover:text-primary"
              title="Back to Custom Objects"
              aria-label="Back to Custom Objects"
            >
              <ArrowLeft size={14} />
            </button>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-text-primary truncate">{isNew ? 'Define a new object type' : 'Edit object type'}</div>
              <div className="text-xs text-text-muted truncate">Adding fields here changes the form on every record of this type.</div>
            </div>
          </div>
          <Button size="sm" onClick={onSave} loading={saving}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </div>

        {/* AI generate panel — only on the create flow. Operator describes
            what they want to track in natural language; the AI proposes a
            label + fields and pre-fills the form below. Everything is then
            editable before Save. */}
        {isNew && (
          <Card padded>
            <SectionHeader
              title="Generate with AI"
              subtitle="Describe what you want to track and we'll draft the fields. You can edit any of it before saving."
            />
            <div className="space-y-2">
              <div className="flex justify-end">
                <EnhancePromptButton
                  value={aiBrief}
                  onChange={setAiBrief}
                  context="Custom object type description"
                  disabled={aiBusy}
                  label="Enhance description"
                />
              </div>
              <textarea
                rows={3}
                className={clsx(inputCls, 'resize-none')}
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
                placeholder='e.g. "I want to track salon services with name, category (hair/nails/facial), price, duration, description, and photos."'
                disabled={aiBusy}
              />
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-text-muted">
                  Tip: mention prices, photos, categories, or anything customer-facing. The AI marks those for the WhatsApp bot.
                </div>
                <Button
                  size="sm"
                  onClick={generateViaAi}
                  loading={aiBusy}
                  iconLeft={aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  disabled={aiBusy || !aiBrief.trim()}
                >
                  {aiBusy ? 'Generating…' : 'Generate fields'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Inline help — only on the create flow. Editors who already know
            what they're doing don't need to read this every time. */}
        {isNew && (
          <Card padded>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                <Lightbulb size={18} />
              </div>
              <div className="flex-1 space-y-1.5 text-sm text-text-secondary">
                <p>
                  <strong className="text-text-primary">A type is a data table you design.</strong> Each *record* under
                  this type will be one row — e.g. you create a <em>Service Package</em> type, then add records
                  like "MVP in 6 weeks — $4,500".
                </p>
                <p className="text-xs text-text-muted">
                  Fill the Basics below (label + key + optional description), then add the fields each record
                  should hold. Tick <strong>Include in AI context</strong> on fields the WhatsApp / IG bot
                  should be allowed to quote from. Leave it off for internal data (margins, license numbers).
                </p>
              </div>
              <Link
                to="/custom-objects/examples"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-semibold text-text-primary hover:border-primary hover:text-primary"
              >
                <BookOpen size={13} />
                See examples (clinic, restaurant, software house…)
              </Link>
            </div>
          </Card>
        )}

        {/* Basics */}
        <Card padded>
          <SectionHeader title="Basics" />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Label" required>
              <input
                className={inputCls}
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                  if (isNew && (!key || key === slugify(label))) setKey(slugify(e.target.value));
                }}
                placeholder="Doctor"
              />
            </FormField>
            <FormField label="Plural label" hint="Used in lists and sidebar — defaults to label + s">
              <input
                className={inputCls}
                value={pluralLabel}
                onChange={(e) => setPluralLabel(e.target.value)}
                placeholder="Doctors"
              />
            </FormField>
            <FormField label="Key" hint="Stable machine identifier — lowercase letters/numbers/underscores">
              <input
                className={clsx(inputCls, 'font-mono', !isNew && 'opacity-60')}
                value={key}
                disabled={!isNew}
                onChange={(e) => setKey(slugify(e.target.value))}
                placeholder="doctor"
              />
            </FormField>
            <FormField label="Lucide icon name" hint="Any name from lucide.dev">
              <input
                className={inputCls}
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="Stethoscope"
              />
            </FormField>
          </div>

          <FormField label="Description" hint="Shown on the records list page and used by the AI as type context">
            <div className="mb-1.5 flex justify-end">
              <EnhancePromptButton
                value={description}
                onChange={setDescription}
                context="Custom object type description"
                disabled={saving}
              />
            </div>
            <textarea
              rows={2}
              className={clsx(inputCls, 'resize-none')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>

          <div className="grid gap-2 sm:grid-cols-3">
            <label className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-sm cursor-pointer">
              <input type="checkbox" checked={linkToContact} onChange={(e) => setLinkToContact(e.target.checked)} className="accent-primary" />
              Linked to a Contact
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-sm cursor-pointer">
              <input type="checkbox" checked={linkToLead} onChange={(e) => setLinkToLead(e.target.checked)} className="accent-primary" />
              Linked to a Lead
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-primary" />
              Active (visible in sidebar)
            </label>
          </div>
        </Card>

        {/* Fields */}
        <Card padded>
          <SectionHeader
            title="Fields"
            subtitle="Define what data each record should hold"
            action={
              <Button size="sm" variant="secondary" iconLeft={<Plus size={12} />} onClick={addField}>
                Add field
              </Button>
            }
          />

          {fields.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-text-muted">
              No fields yet. Click "Add field" above.
            </div>
          ) : (
            <div className="space-y-2">
              {fields.map((f, idx) => (
                <div key={idx} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                  {/* Two-row layout — eliminates the prior horizontal squash where
                      the Label input collapsed to near-zero width on narrower
                      viewports. Row 1 (always full width): the Label, which is
                      THE primary thing users come here to set. Row 2: the
                      auto-generated key, the type dropdown, and the delete
                      button — secondary metadata. */}
                  {/* Row 1 — Label (primary) */}
                  <div className="flex items-center gap-2">
                    <div
                      className="flex w-7 shrink-0 flex-col items-center rounded-lg bg-surface/70 text-text-muted"
                      title="Reorder — use ▲ / ▼ to move this field"
                    >
                      <button
                        onClick={() => moveField(idx, -1)}
                        disabled={idx === 0}
                        className="rounded-t-lg px-1 pt-1 hover:bg-surface hover:text-text-primary disabled:opacity-25 disabled:hover:bg-transparent"
                        title="Move up"
                        aria-label="Move field up"
                      ><ChevronUp size={14} /></button>
                      <GripVertical size={12} className="opacity-50" aria-hidden="true" />
                      <button
                        onClick={() => moveField(idx, 1)}
                        disabled={idx === fields.length - 1}
                        className="rounded-b-lg px-1 pb-1 hover:bg-surface hover:text-text-primary disabled:opacity-25 disabled:hover:bg-transparent"
                        title="Move down"
                        aria-label="Move field down"
                      ><ChevronDown size={14} /></button>
                    </div>

                    <div className="flex-1 min-w-0">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                        Label <span className="font-normal lowercase text-text-muted/70">— what users see on the form</span>
                      </label>
                      <input
                        className={clsx(inputCls, 'bg-surface font-medium')}
                        placeholder="e.g. Service Name, Category, Starting Price"
                        value={f.label}
                        onChange={(e) => updateField(idx, {
                          label: e.target.value,
                          key: f.key.startsWith('field_') ? slugify(e.target.value) : f.key,
                        })}
                      />
                    </div>

                    <button
                      onClick={() => removeField(idx)}
                      className="self-end rounded-lg p-2 text-text-muted hover:bg-danger-soft hover:text-danger"
                      title="Delete field"
                      aria-label="Delete field"
                    ><X size={14} /></button>
                  </div>

                  {/* Row 2 — Key + Type (secondary metadata) */}
                  <div className="mt-3 grid gap-2 pl-9 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                        Key <span className="font-normal lowercase text-text-muted/70">— auto-generated, edit only if needed</span>
                      </label>
                      <input
                        className={clsx(inputCls, 'bg-surface font-mono text-xs')}
                        placeholder="auto"
                        value={f.key}
                        onChange={(e) => updateField(idx, { key: slugify(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                        Type <span className="font-normal lowercase text-text-muted/70">— input control</span>
                      </label>
                      <Select
                        className={clsx(inputCls, 'bg-surface')}
                        value={f.type}
                        onChange={(e) => updateField(idx, { type: e.target.value as CustomFieldType })}
                      >
                        {FIELD_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                      </Select>
                    </div>
                  </div>

                  {(f.type === 'select' || f.type === 'multiselect') && (
                    <div className="mt-2">
                      <input
                        className={clsx(inputCls, 'bg-surface')}
                        placeholder="Comma-separated options (e.g. low, medium, high)"
                        value={(f.options ?? []).join(', ')}
                        onChange={(e) => updateField(idx, {
                          options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                        })}
                      />
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        onChange={(e) => updateField(idx, { required: e.target.checked })}
                        className="accent-primary"
                      />
                      Required
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer" title="Show this field's value to the AI when answering customer questions">
                      <input
                        type="checkbox"
                        checked={!!f.includeInAiContext}
                        onChange={(e) => updateField(idx, { includeInAiContext: e.target.checked })}
                        className="accent-primary"
                      />
                      Include in AI context
                    </label>
                    <input
                      className={clsx(inputCls, 'flex-1 bg-surface text-xs')}
                      placeholder="Description (optional)"
                      value={f.description ?? ''}
                      onChange={(e) => updateField(idx, { description: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
